import { useTranslation } from "react-i18next";
import { ShortenedIban } from "@features/Budget/components/ShortenedIban";
import { Button } from "@components/Button";
import { InformationCircleIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useGetTransaction } from "@services/budget/queries";
import { type IBudgetTransaction } from "@services/budget/types";
import { formatPrice } from "@utils/units";
import { TransactionInformationModalRow } from "./components/TransactionInformationModalRow";
import { TransactionInformationModalRowSkeleton } from "./components/TransactionInformationModalRowSkeleton";

interface ITransactionInformationModalProps extends IUseModalProps {
  transaction: IBudgetTransaction;
}

export const TransactionInformationModal: React.FC<
  ITransactionInformationModalProps
> = (props) => {
  const { transaction, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const { data } = useGetTransaction({
    bankAccountTransactionId: transaction.bankAccountTransactionId,
  });

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: InformationCircleIcon }}
      actionButtons={
        <Button variant="secondary" onClick={modal.close} className="w-full">
          {t("budget.breakdown.infoModal.close")}
        </Button>
      }
      title={t("budget.breakdown.infoModal.title")}
      className="md:max-w-[25rem]"
    >
      {!data && (
        <div className="divide-y divide-base-light-grey px-4 md:px-0">
          {Array.from({ length: 6 }).map((_, i) => {
            return <TransactionInformationModalRowSkeleton key={i} />;
          })}
        </div>
      )}
      {data && (
        <div className="divide-y divide-base-light-grey px-4 md:px-0">
          <TransactionInformationModalRow
            label={t("budget.breakdown.infoModal.partner")}
            value={data.payment.partner}
          />
          <TransactionInformationModalRow
            label={t("budget.breakdown.infoModal.transactionDate")}
            value={transaction.payment.transactionDate}
          />
          <TransactionInformationModalRow
            label={t("budget.breakdown.infoModal.paymentDescription")}
            value={
              data.payment.paymentDescription
                ? data.payment.paymentDescription
                : t("budget.breakdown.infoModal.noInfo")
            }
          />
          {data.bankAccount.iban && (
            <TransactionInformationModalRow
              label={t("budget.breakdown.infoModal.iban")}
              value={
                <ShortenedIban
                  iban={data.bankAccount.iban}
                  bankName={data.bankAccount.bankName}
                  className="text-right text-sm font-semibold text-base-black"
                />
              }
            />
          )}
          <TransactionInformationModalRow
            label={t("budget.breakdown.infoModal.amount")}
            value={formatPrice(transaction.payment.amount)}
          />
          <TransactionInformationModalRow
            label={t("budget.breakdown.infoModal.initialCategory")}
            value={
              data.category.initialCategory.customerCategoryName ??
              t(`budget.category.${data.category.initialCategory.categorySlug}`)
            }
          />
          {data.category.initialCategory.categorySlug !==
            data.category.currentCategory.categorySlug && (
            <TransactionInformationModalRow
              label={t("budget.breakdown.infoModal.changedCategory")}
              value={
                data.category.currentCategory.customerCategoryName ??
                t(
                  `budget.category.${data.category.currentCategory.categorySlug}`,
                )
              }
            />
          )}
        </div>
      )}
    </Modal>
  );
};
